import { mesaColorNames, classes, typography } from '@/src/common/theme'
import {
  Platform,
  ScrollView,
  SectionList,
  SectionListData,
  StyleSheet,
  Text,
  View,
} from 'react-native'
import { REWARDS_FILTERS, RewardsFilter } from '../types'
import { RewardsComponentsActivityFeedEmptyState } from './activityFeedEmptyState'
import { RewardsComponentsActivityFeedItem } from './activityFeedItem'
import { PointsLedgerItemQueryResult } from '../gql/getPointsLedger.query'
import { RewardsComponentsActivityFeedSectionHeader } from './activityFeedSectionHeader'
import { RewardsComponentsActivityFeedItemSkeleton } from './activityFeedSkeleton'
import { useMemo } from 'react'
import { RewardsComponentsActivityFeedFooterItem } from './activityFeedFooterItem'
import { useStickyGate } from '../hooks/useStickyGate'
import { DesignSystem } from '@/src/design-system'

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexGrow: 1,
  },
  sheet: {
    backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'],
    flex: 1,
    flexGrow: 1,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingTop: 16,
    paddingHorizontal: 16,
    ...classes.cardShadow,
  },
  title: {
    ...typography['heading/heading3'],
  },
  filterContainer: {
    flexGrow: 0,
    flexDirection: 'row',
    overflow: 'scroll',
    paddingVertical: 8,
    paddingLeft: 16,
    marginHorizontal: -16,
  },
  filterEndPadding: {
    width: 16,
  },
  activityContainer: {
    flex: 1,
  },
  activityItem: {
    marginBottom: 16,
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: mesaColorNames['semantic/bg/disabled #DADDDE'],
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconContainer: {
    marginRight: 12,
  },
  loadingContainer: {
    paddingVertical: 16,
    alignItems: 'center',
  },
})

type ActivityFeedProps = {
  view: RewardsFilter
  onSelectView: (view: RewardsFilter) => void
  isRefreshing: boolean
  isEndReachedLoading: boolean
  onRefresh: () => void
  onEndReached: () => void
  sections: SectionListData<PointsLedgerItemQueryResult>[]
}

export const RewardsComponentsActivityFeed = ({
  view,
  onSelectView,
  isRefreshing,
  isEndReachedLoading,
  onRefresh,
  onEndReached,
  sections,
}: ActivityFeedProps) => {
  // For some reason, the web implementation of SectionList never renders more than initialNumToRender
  // If someone has a better solution, _please_!
  const webRenderHint = useMemo(
    () =>
      Platform.OS === 'web'
        ? sections.reduce((acc, section) => acc + section.data.length, 0)
        : undefined,
    [sections],
  )
  const showLoading = useStickyGate(isEndReachedLoading)

  return (
    <View style={styles.container}>
      <View style={styles.sheet}>
        <Text style={styles.title}>Points Activity</Text>
        <ScrollView horizontal style={styles.filterContainer}>
          {REWARDS_FILTERS.map((key) => (
            <DesignSystem.Chip
              key={key}
              label={key}
              selected={view === key}
              onPress={() => onSelectView(key)}
            />
          ))}
          <View style={styles.filterEndPadding} />
        </ScrollView>

        <SectionList
          key={webRenderHint}
          style={styles.activityContainer}
          showsVerticalScrollIndicator={false}
          scrollEventThrottle={16}
          sections={sections}
          refreshing={isRefreshing}
          onRefresh={onRefresh}
          onEndReached={onEndReached}
          onEndReachedThreshold={0.5}
          ListEmptyComponent={showLoading ? undefined : <RewardsComponentsActivityFeedEmptyState />}
          ListFooterComponent={
            showLoading ? (
              <RewardsComponentsActivityFeedItemSkeleton />
            ) : (
              <RewardsComponentsActivityFeedFooterItem />
            )
          }
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => <RewardsComponentsActivityFeedItem item={item} />}
          renderSectionHeader={({ section }) => (
            <RewardsComponentsActivityFeedSectionHeader title={section.title} />
          )}
        />
      </View>
    </View>
  )
}
